import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import AppInstallSection from "@/components/app/AppInstallSection";
import PartnerInviteModal from "@/components/app/PartnerInviteModal";
import { useT } from "@/context/LanguageContext";
import {
  Users,
  DollarSign,
  Package,
  Clock,
  ArrowRight,
  PlusCircle,
  Loader2,
  WalletCards,
  CalendarClock,
  FileText,
  MapPin,
  Sparkles,
  Wallet,
  Quote,
  Camera,
  Trash2,
  Heart,
  Calendar,
  Coins,
  Plane,
  Star,
  UserPlus,
  HeartHandshake,
  ChevronRight,
} from "lucide-react";

/* ═══════════════ WEDDING QUOTES ═══════════════ */
// Fallback hanya dipakai bila server gagal mengirim kutipan.
const QUOTES_FALLBACK = [
  "Pernikahan adalah perjalanan dua hati menjadi satu.",
];

/* ═══════════════ COUNTDOWN HOOK ═══════════════ */
function useCountdown(targetDateStr) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!targetDateStr) return;

    const calculate = () => {
      const target = new Date(targetDateStr).getTime();
      const now = new Date().getTime();
      const diff = target - now;

      if (isNaN(target) || diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    calculate();
    const interval = setInterval(calculate, 1000);
    return () => clearInterval(interval);
  }, [targetDateStr]);

  return timeLeft;
}

export default function Dashboard() {
  const { t } = useT();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [goldPrice, setGoldPrice] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(Date.now());

  // Keterhubungan Pasangan
  const [hasPartner, setHasPartner] = useState(true);
  const [partnerInviteOpen, setPartnerInviteOpen] = useState(false);

  // Popup Rating & Feedback
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackHoverRating, setFeedbackHoverRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const fetchDashboard = async () => {
    try {
      const res = await api.get("/dashboard");
      setData(res.data);
    } catch (error) {
      toast.error("Gagal memuat dashboard");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const checkPartners = async () => {
    try {
      const res = await api.get("/partners");
      const list = res.data?.partners || [];
      setHasPartner(list.length > 0);
    } catch {
      // Jika terjadi kesalahan koneksi, biarkan default true agar tidak mengganggu layout
    }
  };

  useEffect(() => {
    fetchDashboard();
    checkPartners();
    api.get("/gold-price")
      .then((res) => {
        if (res?.data) setGoldPrice(res.data);
      })
      .catch(() => {});

    api.get("/user/feedback/prompt-status")
      .then((res) => {
        if (res?.data?.should_prompt) {
          setShowFeedbackModal(true);
        }
      })
      .catch(() => {});
  }, []);

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackRating) {
      toast.error("Silakan pilih rating bintang terlebih dahulu");
      return;
    }
    setSubmittingFeedback(true);
    try {
      await api.post("/user/feedback", {
        rating: feedbackRating,
        comment: feedbackComment,
      });
      toast.success("Terima kasih atas penilaian & masukan Anda!");
      setShowFeedbackModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim feedback");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleFeedbackDismiss = async () => {
    setShowFeedbackModal(false);
    try {
      await api.post("/user/feedback/dismiss");
    } catch (_) {}
  };

  const weddingDate = data?.wedding?.akad_date || data?.wedding?.wedding_date;
  const { days, hours, minutes, seconds } = useCountdown(weddingDate);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-muted-foreground py-20">
        <p>Data tidak tersedia. Silakan refresh.</p>
      </div>
    );
  }

  const { wedding, progress, budget, savings, guests, vendors, upcoming, this_week } = data;

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran foto maksimal 5 MB");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setUploadingPhoto(true);
    try {
      await api.post("/wedding/photo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto pernikahan berhasil diperbarui");
      setPhotoVersion(Date.now());
      await fetchDashboard();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengunggah foto");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    try {
      await api.delete("/wedding/photo");
      toast.success("Foto pernikahan berhasil dihapus");
      setPhotoVersion(Date.now());
      await fetchDashboard();
    } catch (err) {
      toast.error("Gagal menghapus foto");
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat("id-ID").format(amount || 0);

  const formatShortDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("id-ID", { day: "numeric", month: "short" });
    } catch {
      return dateStr;
    }
  };

  const formatLongDate = (dateStr) => {
    if (!dateStr) return "Belum ditentukan";
    try {
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      return d.toLocaleDateString("id-ID", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const p1 = wedding?.partner1_nickname || wedding?.partner1_name || "Mempelai 1";
  const p2 = wedding?.partner2_nickname || wedding?.partner2_name || "Mempelai 2";
  const coupleNames = `${p1} & ${p2}`;
  const akadDateFormatted = weddingDate ? formatLongDate(weddingDate) : "Belum ditentukan";

  const percent = Math.min(100, Math.max(0, progress?.percent || 0));
  const spentPercent = budget?.budget ? Math.min(100, Math.round((budget.actual / budget.budget) * 100)) : 0;
  const remaining = (budget?.budget || 0) - (budget?.actual || 0);
  const guestPercent = guests?.total ? Math.round((guests.attending / guests.total) * 100) : 0;
  const vendorPercent = vendors?.total ? Math.round((vendors.booked / vendors.total) * 100) : 0;

  // Kutipan harian
  const todayQuote = data?.quote?.text || QUOTES_FALLBACK[0];

  const QUICK_ACTIONS = [
    { label: "Tugas", icon: PlusCircle, path: "/checklist" },
    { label: "Anggaran", icon: WalletCards, path: "/budget" },
    { label: "Jadwal", icon: CalendarClock, path: "/timeline" },
    { label: "Dokumen", icon: FileText, path: "/documents" },
  ];

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8 space-y-5 md:space-y-6 luxury-bg min-h-full">

      {/* ===== BANNER PENGINGAT PASANGAN (COMPACT REDESIGN) ===== */}
      {!hasPartner && (
        <div
          data-testid="unlinked-partner-banner"
          className="rounded-xl sm:rounded-2xl border border-rose-200/70 bg-rose-50/75 p-3 sm:px-4 sm:py-3 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fade-in-up"
        >
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <div className="h-8 w-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
              <HeartHandshake className="h-4 w-4" />
            </div>
            <div className="min-w-0 space-y-0.5">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <span className="text-xs sm:text-sm font-semibold text-rose-950 leading-tight">
                  {t("dash.unlinked_partner_title")}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-rose-100 text-rose-700 border border-rose-200/60 leading-none">
                  {t("dash.unlinked_partner_status")}
                </span>
              </div>
              <p className="text-xs text-rose-800/80 leading-normal line-clamp-2 sm:line-clamp-1">
                {t("dash.unlinked_partner_desc")}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setPartnerInviteOpen(true)}
            className="rounded-full bg-rose-600 hover:bg-rose-700 text-white font-medium text-xs px-3.5 h-8 whitespace-nowrap self-start sm:self-auto shrink-0 gap-1 shadow-xs"
            data-testid="dashboard-invite-partner-btn"
          >
            <span>{t("dash.invite_partner_btn")}</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* ===== HERO CARDS (Countdown + Calon Pengantin) ===== */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5 animate-fade-in-up">
        
        {/* CARD 1: Countdown & Photo Background (lg:col-span-5) */}
        <div className="lg:col-span-5 relative overflow-hidden rounded-2xl border border-border/60 shadow-md min-h-[260px] md:min-h-[280px] flex flex-col justify-between p-5 md:p-6 group bg-stone-900 text-white">
          {/* Background Image / Overlay */}
          {wedding?.has_photo ? (
            <img
              src={`${API_BASE}/wedding/${wedding.wedding_id}/photo?v=${photoVersion}`}
              alt="Foto Pernikahan"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-amber-900/90 via-stone-900 to-neutral-950 flex items-center justify-center">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent" />
            </div>
          )}

          {/* Gradient Dark Overlay for legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/30 pointer-events-none" />

          {/* Top controls: Upload & Delete buttons */}
          <div className="relative z-10 flex items-center justify-between">
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-md text-white border border-white/20 text-xs font-medium cursor-pointer transition-colors shadow-sm">
              {uploadingPhoto ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Camera className="h-3.5 w-3.5" />
              )}
              <span>{wedding?.has_photo ? "Ganti foto" : "Unggah foto"}</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleUploadPhoto}
                disabled={uploadingPhoto}
              />
            </label>

            {wedding?.has_photo && (
              <button
                type="button"
                onClick={handleDeletePhoto}
                className="p-1.5 rounded-full bg-black/40 hover:bg-red-500/80 backdrop-blur-md text-white border border-white/20 transition-colors cursor-pointer shadow-sm"
                title="Hapus foto"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Bottom: Live Countdown Numbers */}
          <div className="relative z-10 mt-auto pt-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-200/90 font-medium mb-3 text-center sm:text-left">
              Hitung Mundur Pernikahan
            </div>
            <div className="grid grid-cols-4 text-center divide-x divide-white/20 bg-black/30 backdrop-blur-md rounded-xl p-3 border border-white/10 shadow-inner">
              <div className="px-1">
                <div className="text-2xl sm:text-3xl font-serif text-white font-normal leading-none">
                  {days}
                </div>
                <div className="text-[9px] tracking-widest uppercase text-white/80 mt-1.5 font-medium">
                  HARI
                </div>
              </div>
              <div className="px-1">
                <div className="text-2xl sm:text-3xl font-serif text-white font-normal leading-none">
                  {hours}
                </div>
                <div className="text-[9px] tracking-widest uppercase text-white/80 mt-1.5 font-medium">
                  JAM
                </div>
              </div>
              <div className="px-1">
                <div className="text-2xl sm:text-3xl font-serif text-white font-normal leading-none">
                  {minutes}
                </div>
                <div className="text-[9px] tracking-widest uppercase text-white/80 mt-1.5 font-medium">
                  MENIT
                </div>
              </div>
              <div className="px-1">
                <div className="text-2xl sm:text-3xl font-serif text-white font-normal leading-none">
                  {seconds}
                </div>
                <div className="text-[9px] tracking-widest uppercase text-white/80 mt-1.5 font-medium">
                  DETIK
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CARD 2: Calon Pengantin & Detail Event (lg:col-span-7) */}
        <Card className="lg:col-span-7 rounded-2xl border-border/60 bg-card/80 backdrop-blur-sm p-5 md:p-6 shadow-sm flex flex-col justify-between">
          <div>
            {/* Header with Circle Heart Icon */}
            <div className="flex items-center gap-4 border-b border-border/40 pb-4 md:pb-5">
              <div className="h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600 shadow-inner">
                <Heart className="h-6 w-6 sm:h-7 sm:w-7 fill-amber-500/20 text-amber-600" />
              </div>
              <div className="min-w-0">
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground block mb-0.5">
                  CALON PENGANTIN
                </span>
                <h2 className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-foreground truncate">
                  {coupleNames}
                </h2>
              </div>
            </div>

            {/* Grid info: Tanggal Akad & Jam Mulai */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 md:mt-5">
              <div className="rounded-xl border border-border/40 bg-muted/30 p-3.5">
                <span className="text-[11px] text-muted-foreground font-medium block mb-1">
                  Tanggal Akad
                </span>
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Calendar className="h-4 w-4 text-amber-600 shrink-0" />
                  <span className="truncate">{akadDateFormatted}</span>
                </div>
              </div>

              <div className="rounded-xl border border-border/40 bg-muted/30 p-3.5">
                <span className="text-[11px] text-muted-foreground font-medium block mb-1">
                  Jam Mulai
                </span>
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Clock className="h-4 w-4 text-amber-600 shrink-0" />
                  <span>{wedding?.akad_time || "08:00 WIB"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Location Footer */}
          <div className="mt-4 pt-3 border-t border-border/40 flex items-center justify-between">
            <div className="flex items-start gap-2.5 min-w-0">
              <div className="h-8 w-8 shrink-0 rounded-lg bg-primary/5 flex items-center justify-center text-primary mt-0.5">
                <MapPin className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <span className="text-[11px] text-muted-foreground font-medium block">
                  Lokasi Akad
                </span>
                <div className="text-sm font-semibold text-foreground truncate">
                  {wedding?.city || wedding?.venue_ceremony || "Bandung"}
                </div>
                {wedding?.venue_ceremony && (
                  <div className="text-xs text-muted-foreground truncate mt-0.5">
                    {wedding.venue_ceremony}
                  </div>
                )}
              </div>
            </div>
            <Button variant="ghost" size="sm" className="text-xs text-primary shrink-0" onClick={() => navigate("/settings")}>
              Ubah detail
            </Button>
          </div>
        </Card>

      </section>

      {/* ===== STAT CARDS — 4 horizontal ===== */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 animate-fade-in-up">
        {/* Budget */}
        <button type="button" onClick={() => navigate("/budget")} className="text-left group">
          <Card className="rounded-2xl border-border/60 bg-card/80 backdrop-blur-sm h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="h-8 w-8 shrink-0 rounded-xl flex items-center justify-center bg-emerald-500/10 text-emerald-600">
                  <DollarSign strokeWidth={1.5} className="h-4 w-4" />
                </div>
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Anggaran</span>
              </div>
              <div className="text-xl md:text-2xl font-bold tracking-tight">Rp {formatCurrency(budget?.budget || 0)}</div>
              <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500/70 to-emerald-500 animate-slide-in-right transition-all duration-700"
                  style={{ width: `${spentPercent}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-muted-foreground">Terpakai Rp {formatCurrency(budget?.actual || 0)}</span>
                <span className="text-[11px] text-emerald-600 font-medium">Sisa Rp {formatCurrency(remaining > 0 ? remaining : 0)}</span>
              </div>
            </CardContent>
          </Card>
        </button>

        {/* Guests */}
        <button type="button" onClick={() => navigate("/guests")} className="text-left group">
          <Card className="rounded-2xl border-border/60 bg-card/80 backdrop-blur-sm h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="h-8 w-8 shrink-0 rounded-xl flex items-center justify-center bg-sky-500/10 text-sky-600">
                  <Users strokeWidth={1.5} className="h-4 w-4" />
                </div>
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Tamu</span>
              </div>
              <div className="text-xl md:text-2xl font-bold tracking-tight">{guests?.attending || 0}<span className="text-muted-foreground/50 font-medium"> / {guests?.total || 0}</span></div>
              <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-sky-500/70 to-sky-500 animate-slide-in-right transition-all duration-700"
                  style={{ width: `${guestPercent}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-muted-foreground">Konfirmasi hadir</span>
                <span className="text-[11px] text-muted-foreground">{guests?.pending || 0} menunggu</span>
              </div>
            </CardContent>
          </Card>
        </button>

        {/* Vendors */}
        <button type="button" onClick={() => navigate("/vendors")} className="text-left group">
          <Card className="rounded-2xl border-border/60 bg-card/80 backdrop-blur-sm h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="h-8 w-8 shrink-0 rounded-xl flex items-center justify-center bg-violet-500/10 text-violet-600">
                  <Package strokeWidth={1.5} className="h-4 w-4" />
                </div>
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Vendor</span>
              </div>
              <div className="text-xl md:text-2xl font-bold tracking-tight">{vendors?.booked || 0}<span className="text-muted-foreground/50 font-medium"> / {vendors?.total || 0}</span></div>
              <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-violet-500/70 to-violet-500 animate-slide-in-right transition-all duration-700"
                  style={{ width: `${vendorPercent}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-muted-foreground">Dibooking</span>
                <span className="text-[11px] text-muted-foreground">{(vendors?.total || 0) - (vendors?.booked || 0)} tersisa</span>
              </div>
            </CardContent>
          </Card>
        </button>

        {/* Savings */}
        <button type="button" onClick={() => navigate("/savings")} className="text-left group">
          <Card className="rounded-2xl border-border/60 bg-card/80 backdrop-blur-sm h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="h-8 w-8 shrink-0 rounded-xl flex items-center justify-center bg-amber-500/10 text-amber-600">
                  <Wallet strokeWidth={1.5} className="h-4 w-4" />
                </div>
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Tabungan</span>
              </div>
              <div className="text-xl md:text-2xl font-bold tracking-tight">Rp {formatCurrency(savings?.total || 0)}</div>
              {savings?.target > 0 ? (
                <>
                  <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-amber-500/70 to-amber-500 animate-slide-in-right transition-all duration-700"
                      style={{ width: `${Math.min(100, Math.round((savings.total / savings.target) * 100))}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-muted-foreground">Terkumpul</span>
                    <span className="text-[11px] text-amber-600 font-medium">Target Rp {formatCurrency(savings.target)}</span>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[11px] text-muted-foreground">Belum ada target</span>
                  <span className="text-[11px] text-amber-600 font-medium">Atur target</span>
                </div>
              )}
            </CardContent>
          </Card>
        </button>
      </section>

      {/* Honeymoon Progress Card */}
      {data.honeymoon && (
        <section className="animate-fade-in-up">
          <button
            type="button"
            onClick={() => navigate("/honeymoon")}
            className="w-full text-left group"
            data-testid="dashboard-honeymoon-card"
          >
            <Card className="rounded-2xl border border-border/70 bg-card/80 backdrop-blur-sm p-4 md:p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                  <div className="h-10 w-10 shrink-0 rounded-2xl bg-amber-500/10 text-amber-600 flex items-center justify-center font-bold">
                    <Plane className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Honeymoon Progress</span>
                      <Badge variant="outline" className="text-[10px]">
                        {data.honeymoon.status}
                      </Badge>
                    </div>
                    <h3 className="font-serif text-lg font-bold text-foreground mt-0.5">
                      {data.honeymoon.title}
                    </h3>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3.5 w-3.5 text-amber-500" /> {data.honeymoon.destination}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6 text-xs w-full md:w-auto justify-between md:justify-end border-t md:border-t-0 border-border/40 pt-3 md:pt-0">
                  {data.honeymoon.countdown_days !== null && data.honeymoon.countdown_days >= 0 && (
                    <div>
                      <span className="text-muted-foreground block text-[10px]">Countdown</span>
                      <span className="font-bold text-amber-600">{data.honeymoon.countdown_days} Hari lagi</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground block text-[10px]">Budget Terpakai</span>
                    <span className="font-semibold text-foreground">Rp {formatCurrency(data.honeymoon.budget_used || 0)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block text-[10px]">Checklist</span>
                    <span className="font-semibold text-foreground">{data.honeymoon.checklist_completed}/{data.honeymoon.checklist_total}</span>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center group-hover:translate-x-1 transition-transform">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </Card>
          </button>
        </section>
      )}

      {/* ===== MAIN 2-COLUMN GRID ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-5">

        {/* LEFT COLUMN (60%) — Quick Actions + Quote */}
        <div className="lg:col-span-3 space-y-4 md:space-y-5 animate-fade-in-up" style={{ animationDelay: "100ms" }}>
          {/* Quick Actions */}
          <section className="space-y-3">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">Aksi Cepat</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {QUICK_ACTIONS.map((qa) => (
                <button
                  key={qa.path}
                  type="button"
                  onClick={() => navigate(qa.path)}
                  className="flex items-center gap-2.5 rounded-xl border border-border/60 bg-card/80 px-3.5 py-3 text-sm font-medium shadow-sm hover:border-primary/40 hover:bg-primary/5 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                >
                  <qa.icon strokeWidth={1.5} className="h-4 w-4 text-primary shrink-0" />
                  <span className="truncate">{qa.label}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Card Harga Emas Mas Kawin */}
          <section className="rounded-2xl border border-amber-500/20 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent backdrop-blur-sm p-4 md:p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold">
                  <Coins className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Harga Emas Mas Kawin (Antam 1g)</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-semibold">
                      +{goldPrice?.change_pct || 0.45}%
                    </span>
                  </div>
                  <div className="text-xl font-bold text-amber-700 dark:text-amber-300 mt-0.5">
                    {goldPrice?.formatted_sell || "Rp 2.457.900"}
                    <span className="text-xs font-normal text-muted-foreground ml-2">Buyback: {goldPrice?.formatted_buyback || "Rp 2.212.110"}</span>
                  </div>
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground italic max-w-xs leading-tight">
                💡 {goldPrice?.tips || "Membeli emas secara bertahap sejak jauh hari membantu menjaga nilai tabungan mas kawin dari inflasi."}
              </div>
            </div>
          </section>

          {/* Daily Quote */}
          <section className="rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm p-4 md:p-5">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 shrink-0 rounded-xl flex items-center justify-center bg-primary/5 text-primary/60">
                <Quote strokeWidth={1.5} className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm italic text-muted-foreground leading-relaxed">"{todayQuote}"</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mt-2">— Kutipan Hari Ini</p>
              </div>
            </div>
          </section>
        </div>

        {/* RIGHT COLUMN (40%) — Donut Progress + Savings */}
        <div className="lg:col-span-2 space-y-4 md:space-y-5 animate-fade-in-up" style={{ animationDelay: "200ms" }}>
          {/* Global Progress Donut */}
          <Card className="rounded-2xl border-border/60 bg-card/80 backdrop-blur-sm h-full">
            <CardContent className="p-5 md:p-6 h-full flex flex-col items-center justify-center">
              <DonutChart percent={percent} />
              <p className="mt-3 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Progress Persiapan</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ===== BOTTOM SECTION — Full Width ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-5">

        {/* This Week's Activities */}
        <Card className="rounded-2xl border-border/60 bg-card/80 backdrop-blur-sm lg:col-span-3 animate-fade-in-up" style={{ animationDelay: "300ms" }}>
          <div className="p-4 md:p-5 border-b border-border/40">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">Minggu Ini</h3>
                <Badge variant="secondary" className="rounded-full text-[10px] px-1.5 py-0">{this_week?.length || 0}</Badge>
              </div>
              <Button variant="link" className="px-0 text-primary text-xs h-auto" onClick={() => navigate("/checklist")}>
                Lihat semua <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </div>
          <CardContent className="p-0">
            {this_week && this_week.length > 0 ? (
              <div className="divide-y divide-border/40">
                {this_week.slice(0, 4).map((task) => (
                  <div
                    key={task.task_id}
                    className="flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-muted/40 transition-colors duration-150 cursor-pointer"
                    onClick={() => navigate("/checklist")}
                  >
                    <span className={`h-2 w-2 shrink-0 rounded-full ${task.status === "completed" ? "bg-emerald-500" : "bg-amber-400"}`} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${task.status === "completed" ? "line-through text-muted-foreground" : ""}`}>{task.title}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] text-muted-foreground">{task.category || "Planning"}</span>
                      {task.due_date && (
                        <span className="text-[11px] text-muted-foreground">{formatShortDate(task.due_date)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm text-muted-foreground">Tidak ada tugas minggu ini</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card className="rounded-2xl border-border/60 bg-card/80 backdrop-blur-sm lg:col-span-2 animate-fade-in-up" style={{ animationDelay: "400ms" }}>
          <div className="p-4 md:p-5 border-b border-border/40">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold">Acara Mendekat</h3>
              </div>
              <Button variant="link" className="px-0 text-primary text-xs h-auto" onClick={() => navigate("/timeline")}>
                Kelola <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </div>
          <CardContent className="p-0">
            {upcoming && upcoming.length > 0 ? (
              <div className="divide-y divide-border/40">
                {upcoming.slice(0, 3).map((event) => {
                  let dayNum = "-", mon = "";
                  try {
                    const d = new Date(event.date);
                    if (!isNaN(d)) {
                      dayNum = d.getDate();
                      mon = d.toLocaleDateString("id-ID", { month: "short" }).toUpperCase();
                    }
                  } catch {}
                  return (
                    <div
                      key={event.event_id}
                      className="flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-muted/40 transition-colors duration-150 cursor-pointer"
                      onClick={() => navigate("/timeline")}
                    >
                      <div className="h-12 w-12 shrink-0 rounded-xl bg-primary/5 border border-primary/15 flex flex-col items-center justify-center leading-none">
                        <span className="text-base font-bold text-primary">{dayNum}</span>
                        <span className="text-[9px] uppercase text-muted-foreground mt-0.5">{mon}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{event.title}</div>
                        {event.location && (
                          <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3 shrink-0" /> {event.location}
                          </div>
                        )}
                      </div>
                      {event.category && <Badge variant="outline" className="text-[10px] shrink-0 rounded-full hidden sm:inline-flex">{event.category}</Badge>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm text-muted-foreground">Belum ada acara terjadwal</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* App Install Guide Section */}
      <AppInstallSection />

      {/* POPUP MODAL FEEDBACK & RATING */}
      <Dialog open={showFeedbackModal} onOpenChange={(open) => { if (!open) handleFeedbackDismiss(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif text-center">Beri Ulasan untuk Weddly ✨</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-sm text-center text-muted-foreground">
              Bagaimana pengalaman Anda sejauh ini dalam merencanakan pernikahan bersama Weddly? Masukan Anda sangat berharga bagi kami.
            </p>

            {/* Bintang Rating */}
            <div className="flex justify-center items-center gap-2 py-2">
              {[1, 2, 3, 4, 5].map((star) => {
                const isFilled = (feedbackHoverRating || feedbackRating) >= star;
                return (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFeedbackRating(star)}
                    onMouseEnter={() => setFeedbackHoverRating(star)}
                    onMouseLeave={() => setFeedbackHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110 focus:outline-none"
                    aria-label={`Bintang ${star}`}
                  >
                    <Star
                      className={`h-8 w-8 ${
                        isFilled
                          ? "fill-amber-400 text-amber-400 drop-shadow-sm"
                          : "text-muted-foreground/30 hover:text-amber-300"
                      }`}
                    />
                  </button>
                );
              })}
            </div>

            {/* Input Pesan Ulasan */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Ulasan & Masukan (Opsional)</label>
              <Textarea
                placeholder="Tulis ulasan, saran, atau kendala Anda di sini..."
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                rows={3}
                className="resize-none"
              />
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handleFeedbackDismiss}
                disabled={submittingFeedback}
              >
                Nanti Saja
              </Button>
              <Button
                type="button"
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700"
                onClick={handleFeedbackSubmit}
                disabled={submittingFeedback}
              >
                {submittingFeedback ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Kirim Feedback
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Undang Pasangan */}
      <PartnerInviteModal
        open={partnerInviteOpen}
        onOpenChange={setPartnerInviteOpen}
        defaultName={wedding?.partner2_nickname || wedding?.partner2_name || ""}
        onSuccess={() => {
          setHasPartner(true);
          checkPartners();
        }}
      />
    </div>
  );
}

/* ═══════════════ DONUT CHART SVG ═══════════════ */
function DonutChart({ percent, size = 140, stroke = 10 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, percent)) / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="donutGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#EAD08C" />
            <stop offset="55%" stopColor="#D4AF5E" />
            <stop offset="100%" stopColor="#A88434" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-muted" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          strokeWidth={stroke} strokeLinecap="round"
          stroke="url(#donutGrad)"
          strokeDasharray={c} strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold tracking-tight">{percent}%</span>
        <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-0.5">Selesai</span>
      </div>
    </div>
  );
}
